import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { FolderItem, FOLDER_COLORS } from 'mock/folder.data';

interface Props {
  open: boolean;
  onClose: () => void;
  onMove: (targetFolderId: string | null) => void;
  itemName: string;
  currentFolderId: string | null;
  allFolders: FolderItem[];
  excludeIds: string[];
}

const UNSELECTED = '__unselected__';

function getFolderDepth(folderId: string, folders: FolderItem[]): number {
  let depth = 0;
  let cur = folders.find((f) => f.id === folderId);
  while (cur?.parentId) {
    depth++;
    cur = folders.find((f) => f.id === cur!.parentId);
  }
  return depth;
}

function buildPath(folderId: string | null, folders: FolderItem[]): string {
  if (!folderId) return 'Thư viện của tôi';
  const parts: string[] = [];
  let cur = folders.find((f) => f.id === folderId);
  while (cur) {
    parts.unshift(cur.name);
    cur = cur.parentId ? folders.find((f) => f.id === cur!.parentId) : undefined;
  }
  return ['Thư viện của tôi', ...parts].join(' › ');
}

function sortTree(folders: FolderItem[]): FolderItem[] {
  const result: FolderItem[] = [];
  const add = (parentId: string | null) => {
    folders.filter((f) => f.parentId === parentId).forEach((f) => {
      result.push(f);
      add(f.id);
    });
  };
  add(null);
  return result;
}

const FolderIcon: React.FC<{ colorId: string }> = ({ colorId }) => {
  const { startColor, endColor } = FOLDER_COLORS[colorId] ?? FOLDER_COLORS['3'];
  return (
    <svg width="22" height="18" viewBox="0 0 122 90" fill="none">
      <defs>
        <linearGradient id={`mi-${colorId}`} x1="61" y1="0" x2="61" y2="90" gradientUnits="userSpaceOnUse">
          <stop stopColor={startColor} /><stop offset="1" stopColor={endColor} />
        </linearGradient>
      </defs>
      <path d="M120.085 14.1861V83.5406C120.085 87.1075 117.184 90 113.607 90H8.55106C4.97387 90 2.073 87.1075 2.073 83.5406V6.45939C2.073 2.89252 4.97387 0 8.55106 0H31.6246C33.6237 0 35.5114 0.921109 36.7384 2.49462L40.8196 7.72672H113.607C117.184 7.72672 120.085 10.6192 120.085 14.1861Z" fill={`url(#mi-${colorId})`} />
    </svg>
  );
};

const MoveModal: React.FC<Props> = ({ open, onClose, onMove, itemName, currentFolderId, allFolders, excludeIds }) => {
  const [selected, setSelected] = useState<string>(UNSELECTED);

  useEffect(() => {
    if (open) setSelected(UNSELECTED);
  }, [open]);

  const userFolders = allFolders.filter((f) => !f.isAdminUpload);
  const excludeSet = new Set(excludeIds);
  const available = sortTree(userFolders).filter((f) => !excludeSet.has(f.id));

  const selectedFolderId: string | null | undefined =
    selected === UNSELECTED ? undefined : selected === '__root__' ? null : selected;

  const canMove =
    selectedFolderId !== undefined &&
    selectedFolderId !== currentFolderId;

  const handleConfirm = () => {
    onMove(selectedFolderId as string | null);
    onClose();
  };

  const pathText =
    selectedFolderId === undefined
      ? 'Chọn thư mục để hiển thị đường dẫn'
      : buildPath(selectedFolderId, userFolders);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Di chuyển "${itemName}"`}
      footer={null}
      width={480}
      className="move-modal-wrapper"
    >
      <div className="move-modal">
        <div className="move-modal__list">
          <div
            className={`move-modal__item${selected === '__root__' ? ' move-modal__item--selected' : ''}${currentFolderId === null ? ' move-modal__item--current' : ''}`}
            onClick={() => setSelected('__root__')}
            role="button"
            tabIndex={0}
          >
            <svg width="22" height="18" viewBox="0 0 24 24" fill="none" stroke="#E9B460" strokeWidth="1.8" strokeLinecap="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            <span className="move-modal__item-name">Thư viện của tôi</span>
            {currentFolderId === null && <span className="move-modal__item-hint">Hiện tại</span>}
          </div>

          {available.map((f) => {
            const depth = getFolderDepth(f.id, userFolders);
            const isCurrent = f.id === currentFolderId;
            return (
              <div
                key={f.id}
                className={`move-modal__item${selected === f.id ? ' move-modal__item--selected' : ''}${isCurrent ? ' move-modal__item--current' : ''}`}
                style={{ paddingLeft: 16 + depth * 20 }}
                onClick={() => setSelected(f.id)}
                role="button"
                tabIndex={0}
              >
                <FolderIcon colorId={f.colorId} />
                <span className="move-modal__item-name">{f.name}</span>
                {isCurrent && <span className="move-modal__item-hint">Hiện tại</span>}
              </div>
            );
          })}
        </div>

        <div className="move-modal__footer">
          <span className="move-modal__path">{pathText}</span>
          <div className="move-modal__actions">
            <button className="move-modal__btn" onClick={onClose}>Hủy</button>
            <button
              className="move-modal__btn move-modal__btn--primary"
              disabled={!canMove}
              onClick={handleConfirm}
            >
              Di chuyển
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MoveModal;
